import type {IssueTimelineItemsItemType} from '@github-ui/server-persisted-queries/types/IssueTimelineItems'

import type {TimelineItemTypes} from './fetch-timeline-items'

/**
 * The Activity filter groups timeline item types into categories. Types the timeline never shows,
 * like mentions, have no category.
 */
export const ACTIVITY_FILTER_CATEGORIES = [
  {id: 'comments', label: 'Comments', itemTypes: ['ISSUE_COMMENT']},
  {
    id: 'statusUpdates',
    label: 'Status updates',
    itemTypes: [
      'CLOSED_EVENT',
      'REOPENED_EVENT',
      'PROJECT_V2_ITEM_STATUS_CHANGED_EVENT',
      'CONVERTED_TO_DISCUSSION_EVENT',
      'CONVERTED_FROM_DRAFT_EVENT',
      'MARKED_AS_DUPLICATE_EVENT',
      'UNMARKED_AS_DUPLICATE_EVENT',
    ],
  },
  {
    id: 'metadata',
    label: 'Labels and metadata',
    itemTypes: [
      'ASSIGNED_EVENT',
      'UNASSIGNED_EVENT',
      'ISSUE_FIELD_ADDED_EVENT',
      'ISSUE_FIELD_CHANGED_EVENT',
      'ISSUE_FIELD_REMOVED_EVENT',
      'ISSUE_TYPE_ADDED_EVENT',
      'ISSUE_TYPE_CHANGED_EVENT',
      'ISSUE_TYPE_REMOVED_EVENT',
      'LABELED_EVENT',
      'UNLABELED_EVENT',
      'MILESTONED_EVENT',
      'DEMILESTONED_EVENT',
      'RENAMED_TITLE_EVENT',
      'TRANSFERRED_EVENT',
    ],
  },
  {
    id: 'relationships',
    label: 'Relationships',
    itemTypes: [
      'PARENT_ISSUE_ADDED_EVENT',
      'PARENT_ISSUE_REMOVED_EVENT',
      'SUB_ISSUE_ADDED_EVENT',
      'SUB_ISSUE_REMOVED_EVENT',
      'BLOCKED_BY_ADDED_EVENT',
      'BLOCKED_BY_REMOVED_EVENT',
      'BLOCKING_ADDED_EVENT',
      'BLOCKING_REMOVED_EVENT',
      'RELATES_TO_ADDED_EVENT',
      'RELATES_TO_REMOVED_EVENT',
      'ADDED_TO_PROJECT_V2_EVENT',
      'REMOVED_FROM_PROJECT_V2_EVENT',
    ],
  },
  {
    id: 'references',
    label: 'References',
    itemTypes: ['CROSS_REFERENCED_EVENT', 'REFERENCED_EVENT', 'CONNECTED_EVENT', 'DISCONNECTED_EVENT'],
  },
  {
    id: 'moderation',
    label: 'Moderation',
    itemTypes: [
      'LOCKED_EVENT',
      'UNLOCKED_EVENT',
      'PINNED_EVENT',
      'UNPINNED_EVENT',
      'ISSUE_COMMENT_PINNED_EVENT',
      'ISSUE_COMMENT_UNPINNED_EVENT',
      'COMMENT_DELETED_EVENT',
      'USER_BLOCKED_EVENT',
    ],
  },
] as const satisfies ReadonlyArray<{id: string; label: string; itemTypes: readonly IssueTimelineItemsItemType[]}>

export type ActivityFilterCategory = (typeof ACTIVITY_FILTER_CATEGORIES)[number]['id']

export const ACTIVITY_FILTER_CATEGORY_IDS: readonly ActivityFilterCategory[] = ACTIVITY_FILTER_CATEGORIES.map(
  category => category.id,
)

const CATEGORY_IDS: ReadonlySet<string> = new Set(ACTIVITY_FILTER_CATEGORY_IDS)

export function isActivityFilterCategory(value: unknown): value is ActivityFilterCategory {
  return typeof value === 'string' && CATEGORY_IDS.has(value)
}

/**
 * In menu order, without duplicates, and empty when every category is picked, since both show the whole
 * timeline. Also the shape sent with analytics, so combinations compare equal.
 */
export function normalizeActivityFilters(filters: readonly ActivityFilterCategory[]): ActivityFilterCategory[] {
  const normalized = ACTIVITY_FILTER_CATEGORY_IDS.filter(id => filters.includes(id))
  return normalized.length === ACTIVITY_FILTER_CATEGORY_IDS.length ? [] : normalized
}

/** No categories, or all of them, reads the whole timeline. */
export function itemTypesForActivityFilters(filters: readonly ActivityFilterCategory[]): TimelineItemTypes | undefined {
  const selected = ACTIVITY_FILTER_CATEGORIES.filter(category => filters.includes(category.id))
  if (selected.length === 0 || selected.length === ACTIVITY_FILTER_CATEGORIES.length) return undefined

  return selected.flatMap(category => category.itemTypes)
}

/** `ProjectV2ItemStatusChangedEvent` -> `PROJECT_V2_ITEM_STATUS_CHANGED_EVENT`, as the server names them. */
export function timelineItemTypeFor(typename: string): IssueTimelineItemsItemType {
  return typename.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase() as IssueTimelineItemsItemType
}
